// #REV_WK_DRILL_V1 (2026-10-29) — Bill: on the WK (Weekly by Supervisor) screen, reviews keep needing
// "which customer / day / entry is behind this number" and "why did this bore take so long" — he was
// toggling over to INV Input and Daily to find out. Now:
//   • click a supervisor's REV cell → WeekLinesModal: every invoice line behind that number
//   • 📒 Daily on any line → DailyJobModal: that job's Daily Log (stages, delays, comments) with the
//     SUP's start → PULL summary; "◀ Back to lines" returns to the lines, ✕ returns straight to the weekly grid
//   • ⬇ Year XLSX → exportRevenueYearXlsx: the whole year by week (REV · LF · BORE per week, live YTD
//     formulas), every bore's start → pull with progress-vs-pull billing, and every line flat.
// Read-only throughout — nothing here writes to Supabase.
import { useState, useEffect, useMemo } from "react";
import { sg, _revNum, C, Btn } from "./appShared";

const _up = s => String(s || '').trim().toUpperCase();
export const drillNormDuc = s => _up(s).replace(/^CP-/, '').replace(/-CP$/, '');
const _mdy = s => { const m = String(s || '').match(/(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})/); if (!m) return null; let y = m[3]; if (y.length === 2) y = '20' + y; return new Date(Number(y), Number(m[1]) - 1, Number(m[2]), 12, 0, 0, 0); };
const _iso = s => { const m = String(s || '').match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0, 0) : null; };
const _fmt = d => d ? `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${String(d.getFullYear()).slice(-2)}` : '';
const _days = (a, b) => Math.round((b - a) / 86400000);
const _money = v => (typeof v === 'number' && !Number.isNaN(v)) ? `$${Math.round(v).toLocaleString()}` : '';
const _supKey = s => _up(s).replace(/\./g, '').replace(/\s+/g, ' ');

// Daily-log jobs are keyed by DUC, but a job added through Add Job can live under a custom-NNNN key
// with the real number in displayDuc — match on any of key / duc / displayDuc, CP stripped.
export const findDailyJob = (logs, duc) => {
  const d = drillNormDuc(duc); if (!d || !logs) return null;
  if (logs[duc]) return { key: duc, job: logs[duc] };
  for (const [k, j] of Object.entries(logs)) {
    if (drillNormDuc(k) === d || drillNormDuc(j && j.duc) === d || drillNormDuc(j && j.displayDuc) === d) return { key: k, job: j };
  }
  return null;
};

let _logsCache = null, _logsAt = 0;
const loadDailyLogs = async () => {
  if (_logsCache && Date.now() - _logsAt < 60000) return _logsCache;
  const v = await sg('daily-logs'); _logsCache = (v && typeof v === 'object') ? v : {}; _logsAt = Date.now(); return _logsCache;
};

const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 3000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '4vh 12px', overflowY: 'auto' };
const panel = (w) => ({ background: '#fff', borderRadius: 10, width: '100%', maxWidth: w, boxShadow: '0 20px 50px rgba(0,0,0,0.3)', padding: '14px 16px', boxSizing: 'border-box' });
const th = { textAlign: 'left', fontSize: 10.5, fontWeight: 800, color: '#475569', padding: '5px 7px', borderBottom: '2px solid #CBD5E1', whiteSpace: 'nowrap', background: '#F8FAFC', position: 'sticky', top: 0 };
const td = { fontSize: 12, padding: '5px 7px', borderBottom: '1px solid #E5E7EB', verticalAlign: 'top' };

// ───────────────────────── lines behind one SUP-week cell ─────────────────────────
export function WeekLinesModal({ drill, inv, normName, onClose, onNote }) {
  const [daily, setDaily] = useState(null);   // { duc, sup, date } while the Daily window is open
  const { supKey, supLabel, mon, sun, wkNo, range, note } = drill;
  const lines = useMemo(() => (inv || []).filter(e => {
    const d = _mdy(e.pullDate); return d && d >= mon && d <= sun && normName(e.sup) === supKey;
  }).sort((a, b) => (_mdy(a.pullDate) - _mdy(b.pullDate)) || String(a.invNo).localeCompare(String(b.invNo))), [inv, mon, sun, supKey, normName]);
  const tot = lines.reduce((a, e) => ({ amt: a.amt + _revNum(e.invAmnt), lf: a.lf + _revNum(e.lf), bore: a.bore + _revNum(e.bore) }), { amt: 0, lf: 0, bore: 0 });
  useEffect(() => {
    if (daily) return undefined;   // the Daily window handles Esc while it's open
    const k = (ev) => { if (ev.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k);
  }, [daily, onClose]);
  if (daily) return <DailyJobModal duc={daily.duc} focusSup={daily.sup} focusDate={daily.date} onBack={() => setDaily(null)} onClose={onClose} />;
  return (
    <div style={overlay} onClick={onClose}>
      <div style={panel(1000)} onClick={ev => ev.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
          <div style={{ fontWeight: 900, fontSize: 15, color: '#1E3A8A' }}>{supLabel} · WK {String(wkNo).padStart(2, '0')}</div>
          <div style={{ fontSize: 12, color: C.muted }}>{range} · by pull date · {lines.length} line{lines.length === 1 ? '' : 's'}</div>
          <div style={{ flex: 1 }} />
          {onNote && <Btn onClick={onNote} variant="secondary" small>📝 Note / incentive flag</Btn>}
          <Btn onClick={onClose} variant="secondary" small>✕ Back to weekly</Btn>
        </div>
        {note && <div style={{ fontSize: 11.5, color: '#854D0E', background: '#FEF9C3', border: '1px solid #FDE68A', borderRadius: 6, padding: '5px 9px', marginBottom: 8 }}>📝 {note}</div>}
        {!lines.length ? (
          <div style={{ fontSize: 12.5, color: C.muted, padding: '14px 4px' }}>No invoice lines with a pull date in this week for {supLabel}.</div>
        ) : (
          <div style={{ overflowX: 'auto', maxHeight: '65vh' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead><tr>{['Pull date', 'INV date', 'INV #', 'DUC', 'Customer', 'PM', 'Size', 'LF', 'Bore', 'Amount', '$/LF', 'Note', ''].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {lines.map((e, i) => (
                  <tr key={e.id || i} style={{ background: i % 2 ? '#F9FAFB' : '#fff' }}>
                    <td style={td}>{e.pullDate}</td>
                    <td style={td}>{e.invDate}</td>
                    <td style={{ ...td, fontWeight: 700 }}>{e.invNo}</td>
                    <td style={{ ...td, fontFamily: 'monospace' }}>{e.duc}</td>
                    <td style={td}>{e.customer}</td>
                    <td style={td}>{e.pm}</td>
                    <td style={td}>{e.size}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{_revNum(e.lf) ? Math.round(_revNum(e.lf)).toLocaleString() : ''}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{_revNum(e.bore) || ''}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: _revNum(e.invAmnt) < 0 ? '#B91C1C' : '#111' }}>{_money(_revNum(e.invAmnt))}</td>
                    <td style={{ ...td, textAlign: 'right', color: '#64748B' }}>{_revNum(e.lf) ? `$${(_revNum(e.invAmnt) / _revNum(e.lf)).toFixed(2)}` : ''}</td>
                    <td style={{ ...td, maxWidth: 220, color: '#475569', fontSize: 11 }}>{e.note || ''}</td>
                    <td style={td}><Btn onClick={() => setDaily({ duc: e.duc, sup: supKey, date: _mdy(e.pullDate) })} variant="secondary" small>📒 Daily</Btn></td>
                  </tr>
                ))}
                <tr>
                  <td style={{ ...td, fontWeight: 800 }} colSpan={7}>Total</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 800 }}>{Math.round(tot.lf).toLocaleString()}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 800 }}>{tot.bore || ''}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 800 }}>{_money(tot.amt)}</td>
                  <td style={td} colSpan={3} />
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ───────────────────────── one job's Daily Log ─────────────────────────
export function DailyJobModal({ duc, focusSup, focusDate, onBack, onClose }) {
  const [logs, setLogs] = useState(null);
  const [onlySup, setOnlySup] = useState(true);
  useEffect(() => { let live = true; loadDailyLogs().then(v => { if (live) setLogs(v); }); return () => { live = false; }; }, []);
  const hit = logs ? findDailyJob(logs, duc) : null;
  const job = hit ? hit.job : null;
  const all = useMemo(() => ((job && Array.isArray(job.entries)) ? job.entries : [])
    .map(e => ({ ...e, _d: _iso(e.date), _sup: _supKey(e.sup), _st: _up(e.stage) }))
    .filter(e => e._d).sort((a, b) => a._d - b._d), [job]);
  const supHas = !!focusSup && all.some(e => e._sup === focusSup);
  const rows = useMemo(() => ((onlySup && supHas) ? all.filter(e => e._sup === focusSup) : all), [all, onlySup, supHas, focusSup]);
  // per SUP + bore: start → pull summary over what's showing
  const bores = useMemo(() => {
    const g = {}; rows.forEach(e => { const k = `${e._sup}|${e.bore || '?'}`; (g[k] = g[k] || []).push(e); });
    return Object.entries(g).map(([k, es]) => {
      const [sup, bore] = k.split('|'); const pull = es.find(e => e._st === 'PULL');
      const cnt = {}; es.forEach(e => { const s = e._st || '—'; cnt[s] = (cnt[s] || 0) + 1; });
      const end = pull ? pull._d : es[es.length - 1]._d;
      return { sup, bore, start: es[0]._d, pull: pull ? pull._d : null, cal: _days(es[0]._d, end) + 1, n: es.length, cnt, lf: _revNum(es[0].boreLF) };
    }).sort((a, b) => a.start - b.start);
  }, [rows]);
  const isFocus = (d) => !!focusDate && _days(focusDate, d) === 0;
  useEffect(() => {
    const k = (ev) => { if (ev.key === 'Escape') onBack(); };
    window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k);
  }, [onBack]);
  useEffect(() => { const el = document.getElementById('drill-focus-row'); if (el) el.scrollIntoView({ block: 'center' }); }, [rows]);
  const stBg = st => st === 'DELAY' ? '#FEE2E2' : st === 'PULL' ? '#DCFCE7' : (st === 'MOB' || st === 'SETUP') ? '#E0F2FE' : null;
  return (
    <div style={overlay} onClick={onClose}>
      <div style={panel(1120)} onClick={ev => ev.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
          <Btn onClick={onBack} variant="secondary" small>◀ Back to lines</Btn>
          <div style={{ fontWeight: 900, fontSize: 15, color: '#1E3A8A' }}>📒 Daily · <span style={{ fontFamily: 'monospace' }}>{drillNormDuc(duc)}</span></div>
          {job && <div style={{ fontSize: 12, color: C.muted }}>{job.customer}{job.city ? ` · ${job.city}` : ''}{job.pm ? ` · PM ${job.pm}` : ''}</div>}
          <div style={{ flex: 1 }} />
          {supHas && <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}><input type="checkbox" checked={onlySup} onChange={e => setOnlySup(e.target.checked)} /> {focusSup} only</label>}
          <Btn onClick={onClose} variant="secondary" small>✕ Back to weekly</Btn>
        </div>
        {!logs ? <div style={{ padding: 16, fontSize: 12.5, color: C.muted }}>Loading Daily Log…</div>
          : !job ? <div style={{ padding: 16, fontSize: 12.5, color: C.muted }}>No Daily Log found for {drillNormDuc(duc) || '(no DUC on this line)'}. It may predate the Daily Log, or the job # on the invoice line doesn't match the Daily.</div>
          : (<>
            {job.desc && <div style={{ fontSize: 12, color: '#334155', marginBottom: 6 }}>{job.desc}</div>}
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 11.5, color: '#475569', marginBottom: 8 }}>
              {job.planLF ? <span>Plan LF <b>{Number(job.planLF).toLocaleString()}</b></span> : null}
              {job.planDays ? <span>Plan days <b>{job.planDays}</b></span> : null}
              {job.planRev ? <span>Plan rev <b>{_money(Number(job.planRev))}</b></span> : null}
              {job.planBores ? <span>Plan bores <b>{job.planBores}</b></span> : null}
              {job.pipeSize ? <span>Pipe <b>{job.pipeSize}</b></span> : null}
              {!supHas && focusSup ? <span style={{ color: '#B45309' }}>No daily entries under {focusSup} — showing all crews</span> : null}
            </div>
            {bores.length > 0 && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                {bores.map(b => (
                  <div key={b.sup + b.bore} style={{ border: '1px solid #CBD5E1', borderRadius: 7, padding: '6px 9px', fontSize: 11.5, background: '#F8FAFC' }}>
                    <div style={{ fontWeight: 800, color: '#1E3A8A' }}>{b.sup} · Bore {b.bore}{b.lf ? ` · ${b.lf.toLocaleString()} LF` : ''}</div>
                    <div>Start <b>{_fmt(b.start)}</b> → {b.pull ? <>Pull <b>{_fmt(b.pull)}</b></> : <b style={{ color: '#B45309' }}>not pulled yet</b>} · <b>{b.cal}</b> cal days · {b.n} dailies</div>
                    <div style={{ color: '#64748B' }}>{Object.entries(b.cnt).map(([s, n]) => <span key={s} style={{ marginRight: 7, color: s === 'DELAY' ? '#B91C1C' : undefined, fontWeight: s === 'DELAY' ? 800 : 400 }}>{s} {n}</span>)}</div>
                  </div>
                ))}
              </div>
            )}
            <div style={{ overflow: 'auto', maxHeight: '58vh' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead><tr>{['Date', 'Day', 'SUP', 'Rig', 'Bore', 'Stage', 'Soil', 'Drilled LF', 'Stage total', 'Comment'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
                <tbody>
                  {rows.map((e, i) => {
                    const f = isFocus(e._d);
                    return (
                      <tr key={e.id || i} id={f ? 'drill-focus-row' : undefined} style={{ background: stBg(e._st) || (i % 2 ? '#F9FAFB' : '#fff'), outline: f ? '2px solid #DC2626' : undefined }}>
                        <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: f ? 800 : 400 }}>{_fmt(e._d)}</td>
                        <td style={td}>{e.day}</td>
                        <td style={td}>{e.sup}</td>
                        <td style={td}>{e.rig}</td>
                        <td style={td}>{e.bore}</td>
                        <td style={{ ...td, fontWeight: 700 }}>{e.stage}</td>
                        <td style={td}>{e.soil}</td>
                        <td style={{ ...td, textAlign: 'right' }}>{_revNum(e.drilledLF) || ''}</td>
                        <td style={{ ...td, textAlign: 'right' }}>{_revNum(e.stageTotal) || ''}</td>
                        <td style={{ ...td, fontSize: 11.5, minWidth: 260 }}>{e.comment}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ fontSize: 10.5, color: C.muted, marginTop: 6 }}>Red = DELAY · green = PULL · blue = MOB/SETUP · boxed row = the pull date on the invoice line. Esc goes back to the lines.</div>
          </>)}
      </div>
    </div>
  );
}

// ───────────────────────── whole-year export ─────────────────────────
// Mirrors the WK screen: week = PULL date, ISO weeks (Mon–Sun), roster order, off-roster SUPs under "—".
export async function exportRevenueYearXlsx({ inv, roster, normName, pmCanon, year }) {
  const ExcelJS = (await import('exceljs')).default;
  const logs = await loadDailyLogs();
  const YR = year || new Date().getFullYear();
  const wk1 = (() => { const j4 = new Date(YR, 0, 4, 12); const d = j4.getDay() || 7; j4.setDate(j4.getDate() - (d - 1)); return j4; })();
  const wkOf = d => Math.floor(_days(wk1, d) / 7) + 1;
  const today = new Date();
  const LAST = Math.max(1, Math.min(53, today.getFullYear() === YR ? wkOf(today) : 52));
  const lines = (inv || []).map(e => ({ e, sup: normName(e.sup), d: _mdy(e.pullDate), id: _mdy(e.invDate), duc: drillNormDuc(e.duc), amt: _revNum(e.invAmnt), lf: _revNum(e.lf), bore: _revNum(e.bore) }))
    .filter(l => l.d && wkOf(l.d) >= 1 && wkOf(l.d) <= LAST);
  const typeOf = {}, rigOf = {};
  roster.forEach(g => g.sups.forEach(s => { const k = normName(s.n); typeOf[k] = s.t; rigOf[k] = g.rig; }));
  const others = [...new Set(lines.map(l => l.sup).filter(s => s && !rigOf[s]))].sort((a, b) => (a === 'OTHER') - (b === 'OTHER') || a.localeCompare(b));
  const rowsDef = [...roster.flatMap(g => g.sups.map(s => ({ rig: g.rig, t: s.t, sup: normName(s.n) }))), ...others.map(s => ({ rig: '—', t: '—', sup: s }))];
  const agg = {};
  lines.forEach(l => { const k = `${l.sup}|${wkOf(l.d)}`; const a = (agg[k] = agg[k] || [0, 0, 0]); a[0] += l.amt; a[1] += l.lf; a[2] += l.bore; });

  const wb = new ExcelJS.Workbook(); wb.creator = 'DOPE';
  const fill = c => ({ type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF' + c } });
  const H = fill('C4B5FD'), SUB = fill('EDE9FE'), YF = fill('93C5FD'), TF = fill('BFDBFE'), HI = fill('FEF3C7');
  const B = { bold: true }, CEN = { horizontal: 'center', vertical: 'middle', wrapText: true };
  const MONEY = '"$"#,##0', N0 = '#,##0';
  const L = n => { let s = ''; while (n > 0) { const m = (n - 1) % 26; s = String.fromCharCode(65 + m) + s; n = Math.floor((n - 1) / 26); } return s; };

  // ── Sheet 1: Year by Week ──
  const ws = wb.addWorksheet('Year by Week', { views: [{ state: 'frozen', xSplit: 3, ySplit: 3, zoomScale: 85 }] });
  ws.getCell('A1').value = `DUC — ${YR} REV · LF · BORE BY WEEK, ALL RIGS (week = pull date, Mon–Sun · from DOPE Revenue · pulled ${_fmt(today)})`;
  ws.getCell('A1').font = { bold: true, size: 12 };
  ['RIG', 'TYPE', 'SUP'].forEach((h, i) => { ws.mergeCells(2, i + 1, 3, i + 1); const c = ws.getCell(2, i + 1); c.value = h; c.font = B; c.fill = H; c.alignment = CEN; });
  for (let w = 1; w <= LAST; w++) {
    const c0 = 4 + 3 * (w - 1); const st = new Date(wk1); st.setDate(st.getDate() + 7 * (w - 1)); const en = new Date(st); en.setDate(en.getDate() + 6);
    ws.mergeCells(2, c0, 2, c0 + 2); const c = ws.getCell(2, c0);
    c.value = `WK ${String(w).padStart(2, '0')}  ${_fmt(st).slice(0, 5)}–${_fmt(en).slice(0, 5)}  Q${Math.floor(st.getMonth() / 3) + 1}`; c.font = B; c.fill = H; c.alignment = CEN;
    ['REV', 'LF', 'BORE'].forEach((h, k) => { const x = ws.getCell(3, c0 + k); x.value = h; x.font = { bold: true, size: 9 }; x.fill = SUB; x.alignment = CEN; });
    ws.getColumn(c0).width = 11; ws.getColumn(c0 + 1).width = 6.5; ws.getColumn(c0 + 2).width = 6;
  }
  const Y = 4 + 3 * LAST;
  const YH = ['YTD REV', 'YTD LF', 'YTD BORES', 'WKS BILLED', 'AVG REV / BILLED WK', 'AVG REV / WK', '$ / LF'];
  ws.mergeCells(2, Y, 2, Y + 6); ws.getCell(2, Y).value = `YTD ${YR}`; ws.getCell(2, Y).font = B; ws.getCell(2, Y).fill = YF; ws.getCell(2, Y).alignment = CEN;
  YH.forEach((h, k) => { const x = ws.getCell(3, Y + k); x.value = h; x.font = { bold: true, size: 9 }; x.fill = YF; x.alignment = CEN; ws.getColumn(Y + k).width = 13; });
  ws.getColumn(1).width = 5; ws.getColumn(2).width = 9; ws.getColumn(3).width = 13; ws.getRow(2).height = 30; ws.getRow(3).height = 28;
  const ytd = r => {
    const pick = off => { const a = []; for (let i = 0; i < LAST; i++) a.push(`${L(4 + 3 * i + off)}${r}`); return a; };
    ws.getCell(r, Y).value = { formula: pick(0).join('+') };
    ws.getCell(r, Y + 1).value = { formula: pick(1).join('+') };
    ws.getCell(r, Y + 2).value = { formula: pick(2).join('+') };
    ws.getCell(r, Y + 3).value = { formula: pick(0).map(c => `(N(${c})<>0)`).join('+') };
    ws.getCell(r, Y + 4).value = { formula: `IFERROR(${L(Y)}${r}/${L(Y + 3)}${r},0)` };
    ws.getCell(r, Y + 5).value = { formula: `${L(Y)}${r}/${LAST}` };
    ws.getCell(r, Y + 6).value = { formula: `IFERROR(${L(Y)}${r}/${L(Y + 1)}${r},0)` };
  };
  const putWeeks = (r, source, prefix) => {
    for (let w = 1; w <= LAST; w++) {
      const a = source[`${prefix}|${w}`]; if (!a || !(a[0] || a[1] || a[2])) continue;
      const c0 = 4 + 3 * (w - 1);
      ws.getCell(r, c0).value = Math.round(a[0] * 100) / 100;
      if (a[1]) ws.getCell(r, c0 + 1).value = a[1];
      if (a[2]) ws.getCell(r, c0 + 2).value = a[2];
      if (a[0] >= 300000) for (let k = 0; k < 3; k++) ws.getCell(r, c0 + k).fill = HI;
    }
  };
  let r = 4; const groups = {}; let prevRig = null;
  rowsDef.forEach(d => {
    ws.getCell(r, 1).value = d.rig; ws.getCell(r, 2).value = d.t; ws.getCell(r, 3).value = d.sup; ws.getCell(r, 3).font = B;
    if (prevRig && d.rig !== prevRig) for (let c = 1; c <= Y + 6; c++) ws.getCell(r, c).border = { top: { style: 'medium' } };
    prevRig = d.rig; (groups[d.rig] = groups[d.rig] || []).push(r);
    putWeeks(r, agg, d.sup); ytd(r); r++;
  });
  const supRows = Array.from({ length: r - 4 }, (_, i) => i + 4); r++;
  const totRow = (label, rws, f) => {
    ws.getCell(r, 3).value = label;
    for (let c = 4; c < Y; c++) ws.getCell(r, c).value = { formula: rws.map(x => `${L(c)}${x}`).join('+') };
    ytd(r); for (let c = 1; c <= Y + 6; c++) { ws.getCell(r, c).fill = f; ws.getCell(r, c).font = B; } r++;
  };
  Object.entries(groups).forEach(([g, rws]) => totRow(g === '—' ? 'OTHER TOTAL' : `${g} TOTAL`, rws, TF));
  totRow('WEEK TOTAL', supRows, YF);
  // PM split — same as the bottom block on the screen
  const pmAgg = {};
  lines.forEach(l => { const p = pmCanon(l.e.pm) || '(no PM)'; const k = `${p}|${wkOf(l.d)}`; const a = (pmAgg[k] = pmAgg[k] || [0, 0, 0]); a[0] += l.amt; a[1] += l.lf; a[2] += l.bore; });
  const pms = [...new Set(lines.map(l => pmCanon(l.e.pm) || '(no PM)'))].sort();
  r++; const pmRows = [];
  pms.forEach(p => { ws.getCell(r, 3).value = p; ws.getCell(r, 3).font = B; pmRows.push(r); putWeeks(r, pmAgg, p); ytd(r); r++; });
  if (pmRows.length) totRow('PM TOTAL', pmRows, YF);
  ws.getCell(r + 1, 1).value = 'Yellow = $300K+ week. BORE = bore count as billed (progress lines carry 0). Totals and YTD are live formulas — change a cell and they follow.';
  ws.getCell(r + 1, 1).font = { italic: true, size: 9 };
  for (let rr = 4; rr < r; rr++) for (let c = 4; c <= Y + 6; c++) {
    const h = ws.getCell(3, c).value; const x = ws.getCell(rr, c);
    x.numFmt = ['REV', 'YTD REV', 'AVG REV / BILLED WK', 'AVG REV / WK'].includes(h) ? MONEY : (h === '$ / LF' ? '"$"#,##0.00' : N0);
    if (c < Y && (c - 4) % 3 === 0) x.border = { ...(x.border || {}), left: { style: 'medium' } };
  }

  // ── Sheet 2: every bore with dailies this year — start → pull, and how it was billed ──
  const bores = {};
  Object.entries(logs || {}).forEach(([k, j]) => {
    const jd = drillNormDuc((j && (j.displayDuc || j.duc)) || k);
    ((j && j.entries) || []).forEach(en => {
      const d = _iso(en.date); if (!d || d.getFullYear() !== YR) return;
      const s = normName(en.sup); if (!s) return;
      const key = `${s}|${jd}|${en.bore || '?'}`;
      (bores[key] = bores[key] || { sup: s, duc: jd, bore: en.bore || '?', cust: (j && j.customer) || '', planLF: j && j.planLF, planRev: j && j.planRev, es: [] })
        .es.push({ ...en, _d: d, _st: _up(en.stage) });
    });
  });
  const blist = Object.values(bores).map(b => {
    b.es.sort((x, y) => x._d - y._d); const p = b.es.find(e => e._st === 'PULL');
    b.start = b.es[0]._d; b.pull = p ? p._d : null; b.last = b.es[b.es.length - 1]._d; return b;
  }).sort((a, b) => a.sup.localeCompare(b.sup) || a.start - b.start);
  const boreOf = new Map();
  blist.forEach(b => {
    const prior = blist.filter(x => x.sup === b.sup && x.duc === b.duc && x.pull && x.pull < b.start).map(x => x.pull.getTime());
    const lo = prior.length ? Math.max(...prior) : new Date(YR, 0, 1, 0).getTime();
    const hi = b.pull ? b.pull.getTime() + 3 * 86400000 : Infinity;
    b.bills = lines.filter(l => l.sup === b.sup && l.duc === b.duc && l.d.getTime() > lo && l.d.getTime() <= hi).sort((x, y) => x.d - y.d);
    b.bills.forEach(l => boreOf.set(l, b));
  });
  const ws2 = wb.addWorksheet('Bore Start to Pull', { views: [{ state: 'frozen', xSplit: 4, ySplit: 3 }] });
  ws2.getCell('A1').value = `${YR} BORES — STARTED WHEN → PULLED WHEN, AND HOW IT WAS BILLED (Daily Log + Revenue)`; ws2.getCell('A1').font = { bold: true, size: 12 };
  const h2 = ['SUP', 'RIG', 'TYPE', 'DUC', 'CUSTOMER', 'BORE #', 'BORE LF (Daily)', 'START (1st daily)', 'PULL (Daily PULL)', 'CAL DAYS START→PULL', 'DAILY ENTRIES', 'DELAY DAYS', 'REAM DAYS', 'PILOT DAYS', 'SWAB/CLEAN DAYS',
    'BILLS', 'PROGRESS $ (before pull)', 'PULL $ (on/after pull)', 'STANDBY/MOB/CREDIT $', 'TOTAL BILLED $', '% BILLED BEFORE PULL', 'DAYS START→1st BILL', 'LONGEST GAP BETWEEN BILLS', '$ / CAL DAY', '$ / WORKING DAY (excl DELAY)', 'LF / WORKING DAY', '$ / LF', 'PLAN LF (job)', 'PLAN REV (job)'];
  h2.forEach((h, i) => { const c = ws2.getCell(3, i + 1); c.value = h; c.font = B; c.fill = H; c.alignment = CEN; ws2.getColumn(i + 1).width = [11, 5, 9, 11, 20][i] || 10; });
  ws2.getRow(3).height = 45;
  let r2 = 4;
  blist.forEach(b => {
    const cnt = {}; b.es.forEach(e => { cnt[e._st] = (cnt[e._st] || 0) + 1; });
    const ft = b.bills.filter(l => l.lf > 0);
    const prog = b.pull ? ft.filter(l => l.d < b.pull).reduce((a, l) => a + l.amt, 0) : ft.reduce((a, l) => a + l.amt, 0);
    const pul = b.pull ? ft.filter(l => l.d >= b.pull).reduce((a, l) => a + l.amt, 0) : 0;
    const oth = b.bills.filter(l => !(l.lf > 0)).reduce((a, l) => a + l.amt, 0);
    const pts = [b.start.getTime(), ...[...new Set(b.bills.map(l => l.d.getTime()))].sort((x, y) => x - y), ...(b.pull ? [b.pull.getTime()] : [])];
    let gap = 0; for (let i = 1; i < pts.length; i++) gap = Math.max(gap, Math.round((pts[i] - pts[i - 1]) / 86400000));
    const end = b.pull || b.last;
    const v = [b.sup, rigOf[b.sup] || '—', typeOf[b.sup] || '—', b.duc, b.cust, b.bore, _revNum(b.es[0].boreLF) || null, b.start, b.pull || 'NOT PULLED YET', _days(b.start, end) + 1, b.es.length,
      cnt.DELAY || 0, cnt.REAM || 0, cnt.PILOT || 0, (cnt.SWAB || 0) + (cnt.CLEAN || 0), b.bills.length, prog, pul, oth];
    v.forEach((x, i) => { ws2.getCell(r2, i + 1).value = x; });
    ws2.getCell(r2, 20).value = { formula: `Q${r2}+R${r2}+S${r2}` };
    ws2.getCell(r2, 21).value = { formula: `IFERROR(Q${r2}/(Q${r2}+R${r2}),0)` };
    ws2.getCell(r2, 22).value = b.bills.length ? _days(b.start, b.bills[0].d) : null;
    ws2.getCell(r2, 23).value = gap || null;
    if (b.pull) {
      ws2.getCell(r2, 24).value = { formula: `IFERROR(T${r2}/J${r2},0)` };
      ws2.getCell(r2, 25).value = { formula: `IFERROR(T${r2}/(K${r2}-L${r2}),0)` };
      ws2.getCell(r2, 26).value = { formula: `IFERROR(G${r2}/(K${r2}-L${r2}),0)` };
      ws2.getCell(r2, 27).value = { formula: `IFERROR((Q${r2}+R${r2})/G${r2},0)` };
    }
    ws2.getCell(r2, 28).value = Number(b.planLF) || null; ws2.getCell(r2, 29).value = Number(b.planRev) || null;
    [[7, N0], [8, 'mm/dd/yy'], [9, 'mm/dd/yy'], [17, MONEY], [18, MONEY], [19, MONEY], [20, MONEY], [21, '0%'], [24, MONEY], [25, MONEY], [26, '0.0'], [27, '"$"#,##0.00'], [28, N0], [29, MONEY]]
      .forEach(([c, f]) => { ws2.getCell(r2, c).numFmt = f; });
    r2++;
  });
  if (r2 > 4) ws2.autoFilter = { from: { row: 3, column: 1 }, to: { row: r2 - 1, column: h2.length } };
  ws2.getCell(r2 + 1, 1).value = 'PROGRESS $ = footage billed BEFORE the Daily Log PULL date on that bore (cash in early). PULL $ = footage billed on/after the pull. A line can say "bore 1" in Revenue and still be progress — the Daily PULL date decides. Working day = dailies minus DELAY days. Bores from before the Daily Log was in use have no row here; their lines are on "All Lines".';
  ws2.getCell(r2 + 1, 1).font = { italic: true, size: 9 };

  // ── Sheet 3: every line, classified progress / pull ──
  const ws3 = wb.addWorksheet('All Lines', { views: [{ state: 'frozen', xSplit: 3, ySplit: 1 }] });
  const h3 = ['RIG', 'TYPE', 'SUP', 'PULL DATE', 'WK', 'INV DATE', 'INV #', 'DUC', 'CUSTOMER', 'PM', 'SIZE', 'LF', 'BORE', 'INV $', '$ / LF', 'BILL TYPE', 'BORE # (Daily)', 'BORE START (Daily)', 'DAYS SINCE BORE START', 'DAYS SINCE PREV BILL (same SUP)', 'NOTE'];
  const w3 = [5, 9, 11, 10, 5, 10, 10, 11, 22, 13, 6, 7, 6, 12, 9, 22, 8, 10, 9, 10, 40];
  h3.forEach((h, i) => { const c = ws3.getCell(1, i + 1); c.value = h; c.font = B; c.fill = H; c.alignment = CEN; ws3.getColumn(i + 1).width = w3[i]; });
  ws3.getRow(1).height = 32;
  const sorted = [...lines].sort((a, b) => a.sup.localeCompare(b.sup) || a.d - b.d);
  const PULLF = fill('DCFCE7'), PROGF = fill('FEF3C7');
  let r3 = 2, prevSup = null, prevD = null;
  sorted.forEach(l => {
    if (l.sup !== prevSup) prevD = null;
    const b = boreOf.get(l);
    let t;
    if (l.amt < 0) t = 'CREDIT';
    else if (!(l.lf > 0)) t = 'STANDBY / MOB / OTHER';
    else if (b && b.pull) t = l.d >= b.pull ? 'PULL' : 'PROGRESS';
    else if (b) t = 'PROGRESS (bore not pulled yet)';
    else t = l.bore >= 1 ? 'PULL (no Daily Log)' : 'PROGRESS (no Daily Log)';
    const v = [rigOf[l.sup] || '—', typeOf[l.sup] || '—', l.sup, l.d, wkOf(l.d), l.id, l.e.invNo, l.duc, l.e.customer, l.e.pm, String(l.e.size || '').replace(/"/g, ''), l.lf || null, l.bore || null, l.amt, null, t,
      b ? b.bore : null, b ? b.start : null, b ? _days(b.start, l.d) : null, prevD ? _days(prevD, l.d) : null, l.e.note || ''];
    v.forEach((x, i) => { ws3.getCell(r3, i + 1).value = x; });
    ws3.getCell(r3, 15).value = { formula: `IFERROR(N${r3}/L${r3},"")` };
    [[4, 'mm/dd/yy'], [6, 'mm/dd/yy'], [18, 'mm/dd/yy'], [14, MONEY], [15, '"$"#,##0.00'], [12, N0]].forEach(([c, f]) => { ws3.getCell(r3, c).numFmt = f; });
    if (t.startsWith('PULL')) ws3.getCell(r3, 16).fill = PULLF; else if (t.startsWith('PROGRESS')) ws3.getCell(r3, 16).fill = PROGF;
    prevSup = l.sup; prevD = l.d; r3++;
  });
  ws3.getCell(r3, 13).value = 'TOTAL'; ws3.getCell(r3, 13).font = B;
  ws3.getCell(r3, 14).value = { formula: `SUM(N2:N${Math.max(2, r3 - 1)})` }; ws3.getCell(r3, 14).numFmt = MONEY; ws3.getCell(r3, 14).font = B;
  if (r3 > 2) ws3.autoFilter = { from: { row: 1, column: 1 }, to: { row: r3 - 1, column: h3.length } };

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
  a.download = `DUC_Revenue_${YR}_by_Week_${_fmt(today).replace(/\//g, '')}.xlsx`;
  document.body.appendChild(a); a.click(); setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 1500);
  return { lines: lines.length, bores: blist.length };
}
